import { API_ENDPOINTS } from "../../config/api";
import React, { useState, useEffect } from "react";
import Reveal from "../common/Reveal";
import axios from "axios";
import { COMMON } from "../../config/common";
import toast, { Toaster } from "react-hot-toast";

type Plan = {
  name: string;
  cadence: string;
  price: string;
  mrp: string;
  offerPrice: string;
  description: string;
  highlights: string[];
  isFeatured?: boolean;
  cta: string;
};

const GROUP_PLANS: Plan[] = [
  // First slide: exactly the three you mentioned
  {
    name: "1 class a week · Monthly",
    cadence: "Group 1 – Starter",
    price: "₹999",
    mrp: "₹999",
    offerPrice: "₹999",
    description:
      "A gentle starting point with four 55-minute group classes spread across the month.",
    highlights: [
      "4 group classes (1 class/week)",
      "55 minute sessions",
      "Great to test the waters",
    ],
    cta: "Start with 1 month",
  },
  {
    name: "2 classes a week · Monthly",
    cadence: "Group 2 – Starter",
    price: "₹1499",
    mrp: "₹1499",
    offerPrice: "₹1499",
    description:
      "Eight 55-minute group classes across a month, with two touch points every week for faster comfort on the instrument.",
    highlights: [
      "8 group classes (2 classes/week)",
      "55 minute sessions",
      "Great to fast-track basics",
    ],
    cta: "Fast-track 1 month",
  },
  {
    name: "3 classes a week · Monthly",
    cadence: "Group 3 – Value",
    price: "₹1999",
    mrp: "₹1999",
    offerPrice: "₹1849",
    description:
      "Twelve 55-minute group classes across the month, with three touch points every week for the fastest progress.",
    highlights: [
      "12 group classes (3 classes/week)",
      "55 minute sessions",
      "Great for faster progress",
    ],
    isFeatured: true,
    cta: "Go intensive",
  },

  // Second slide: remaining duration options using same sheet data
  {
    name: "2 classes a week · 2 months",
    cadence: "Group 2 – Value",
    price: "₹2699",
    mrp: "₹2999",
    offerPrice: "₹2699",
    description:
      "Two classes a week for two months so you move faster, with a clear saving on the total fee.",
    highlights: [
      "16 group classes (2 classes/week)",
      "Fee discount applied",
      "Great for faster progress",
    ],
    cta: "Book 2 months",
  },
  {
    name: "1 class a week · 3 months",
    cadence: "Group 1 – Extended",
    price: "₹2125",
    mrp: "₹2500",
    offerPrice: "₹2125",
    description:
      "A three-month journey in a single weekly class format with a 15% fee reduction built-in.",
    highlights: [
      "12 group classes (1 class/week)",
      "15% fee discount applied",
      "Perfect to stay consistent longer",
    ],
    cta: "Reserve 3 months",
  },
  {
    name: "2 classes a week · 3 months",
    cadence: "Group 2 – Extended",
    price: "₹5099",
    mrp: "₹5999",
    offerPrice: "₹5099",
    description:
      "Three months of high-touch group learning with two classes every week and around 15% savings on fees.",
    highlights: [
      "32 group classes (2 classes/week)",
      "Around 15% savings",
      "Ideal for deep, structured progress",
    ],
    cta: "Lock in 3 months",
  },
];

const ONE_ON_ONE_PLANS: Plan[] = [
  {
    name: "Individual • 4 classes",
    cadence: "Personal Start",
    price: "₹1499",
    mrp: "₹1499",
    offerPrice: "₹1499",
    description:
      "Four 35-minute one-on-one sessions to get you started with individual attention.",
    highlights: [
      "4 individual classes",
      "35 minute sessions",
      "Ideal for absolute beginners",
    ],
    cta: "Try 4 classes",
  },
  {
    name: "Individual • 8 classes",
    cadence: "Focused Month",
    price: "₹2999",
    mrp: "₹2999",
    offerPrice: "₹2999",
    description:
      "Eight focused individual sessions with a 10% discount compared to the standard fee.",
    highlights: [
      "8 individual classes",
      "35 minutes sessions",
      "Flexible scheduling",
    ],
    cta: "Book 8 classes",
  },
  {
    name: "Individual • 12 classes",
    cadence: "Deep Dive",
    price: "₹3824",
    mrp: "₹4499",
    offerPrice: "₹3824",
    description:
      "Twelve one-on-one sessions with around 15% savings, ideal for students fully committing for the month.",
    highlights: [
      "12 individual classes",
      "Around 15% savings",
      "Best for steady, deep learning",
    ],
    isFeatured: true,
    cta: "Go all in",
  },
];

declare const window: any;

const Plans: React.FC = () => {
  const [activeTab, setActiveTab] = useState<"group" | "oneOnOne">("group");
  const groupPageSize = 3;
  const [groupPage, setGroupPage] = useState(0);
  const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false);
  const [showFormModal, setShowFormModal] = useState<boolean>(false);
  const [formData, setFormData] = useState<{ name: string; email: string }>({
    name: "",
    email: "",
  });
  const [pendingCheckout, setPendingCheckout] = useState<{
    amount: number;
    planName: string;
    planType: "group" | "oneOnOne";
  } | null>(null);
  const [isProcessingPayment, setIsProcessingPayment] = useState<boolean>(false);

  const sendWelcomeEmail = async (name: string, email: string, planName: string, planType: "group" | "oneOnOne") => {
    const planTypeLabel = planType === "group" ? "Group Batch" : "1-on-1 Session";
    const logoUrl = COMMON.appLogoUrl;
    
    try {
      const emailHtml = `
        <!DOCTYPE html>
        <html lang="en">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Welcome to Ukulele Seekho!</title>
        </head>
        <body style="margin: 0; padding: 0; font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; background-color: #f8f3ed;">
          <table role="presentation" style="width: 100%; border-collapse: collapse; background-color: #f8f3ed;">
            <tr>
              <td align="center" style="padding: 40px 20px;">
                <table role="presentation" style="max-width: 600px; width: 100%; border-collapse: collapse; background-color: #ffffff; border-radius: 32px; overflow: hidden; box-shadow: 0 35px 70px -40px rgba(7, 27, 53, 0.35);">
                  <!-- Logo Header -->
                  <tr>
                    <td style="background: linear-gradient(135deg, #f8f3ed 0%, #f8f3ed 50%, rgba(195, 153, 90, 0.1) 100%); padding: 40px 30px 30px 30px; text-align: center;">
                      <img src="${logoUrl}" alt="Ukulele Seekho Logo" style="max-width: 120px; height: auto; margin: 0 auto 20px auto; display: block; border: 0;" />
                      <h1 style="margin: 0; color: #071B35; font-size: 32px; font-weight: 700; letter-spacing: -0.5px; font-family: 'Lato', sans-serif;">
                        Welcome to Ukulele Seekho! 🎵
                      </h1>
                    </td>
                  </tr>
                  
                  <!-- Content -->
                  <tr>
                    <td style="padding: 40px 30px; background-color: #ffffff;">
                      <p style="margin: 0 0 20px 0; color: #071B35; font-size: 18px; line-height: 1.6; font-weight: 500;">
                        Hi <strong style="color: #071B35;">${name}</strong>,
                      </p>
                      
                      <p style="margin: 0 0 20px 0; color: rgba(7, 27, 53, 0.7); font-size: 16px; line-height: 1.7;">
                        We're absolutely thrilled to have you join our ukulele community! Your journey to mastering this beautiful instrument starts now, and we're here to support you every step of the way.
                      </p>
                      
                      <div style="background: linear-gradient(135deg, #f8f3ed 0%, rgba(195, 153, 90, 0.05) 100%); border-left: 4px solid #C3995A; padding: 24px; margin: 30px 0; border-radius: 12px;">
                        <p style="margin: 0 0 10px 0; color: #071B35; font-size: 12px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.1em; margin-bottom: 8px;">
                          Your Plan
                        </p>
                        <p style="margin: 0 0 8px 0; color: #071B35; font-size: 20px; font-weight: 600;">
                          ${planName}
                        </p>
                        <p style="margin: 0 0 12px 0; color: #C3995A; font-size: 14px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em;">
                          ${planTypeLabel}
                        </p>
                        <p style="margin: 0; color: rgba(7, 27, 53, 0.65); font-size: 14px; line-height: 1.6;">
                          You're all set! We'll be in touch shortly with all the details about your classes, schedule, and how to get started.
                        </p>
                      </div>
                      
                      <p style="margin: 24px 0; color: rgba(7, 27, 53, 0.7); font-size: 16px; line-height: 1.7;">
                        At Ukulele Seekho, we believe that learning music should be fun, engaging, and deeply rewarding. Whether you're picking up the ukulele for the first time or looking to refine your skills, our experienced instructors are dedicated to helping you achieve your musical goals.
                      </p>
                      
                      <div style="margin: 30px 0; padding: 24px; background-color: #f8f3ed; border-radius: 12px;">
                        <p style="margin: 0 0 16px 0; color: #071B35; font-size: 16px; font-weight: 600;">
                          What's Next?
                        </p>
                        <ul style="margin: 0; padding-left: 20px; color: rgba(7, 27, 53, 0.7); font-size: 15px; line-height: 1.8;">
                          <li style="margin-bottom: 8px;">Check your whatsapp/email for class schedule and joining instructions</li>
                          <li style="margin-bottom: 8px;">Prepare your ukulele (or let us know if you need help finding one!)</li>
                          <li style="margin-bottom: 8px;">Keep a notebook and pen handy for notes.</li>
                          <li style="margin-bottom: 0;">Get ready to strum, learn, and have an amazing time!</li>
                        </ul>
                      </div>
                      
                      <p style="margin: 30px 0 20px 0; color: rgba(7, 27, 53, 0.7); font-size: 16px; line-height: 1.7;">
                        If you have any questions or need assistance, don't hesitate to reach out to us. We're here to help!
                      </p>
                      
                      <p style="margin: 30px 0 0 0; color: rgba(7, 27, 53, 0.7); font-size: 16px; line-height: 1.7;">
                        With warm regards,<br>
                        <strong style="color: #071B35; font-weight: 600;">The Ukulele Seekho Team</strong>
                      </p>
                    </td>
                  </tr>
                  
                  <!-- Footer -->
                  <tr>
                    <td style="background-color: #f8f3ed; padding: 30px; text-align: center; border-top: 1px solid rgba(7, 27, 53, 0.1);">
                      <p style="margin: 0 0 10px 0; color: rgba(7, 27, 53, 0.6); font-size: 14px;">
                        © ${new Date().getFullYear()} Ukulele Seekho. All rights reserved.
                      </p>
                      <p style="margin: 0; color: rgba(7, 27, 53, 0.5); font-size: 12px; font-style: italic;">
                        Keep strumming, keep learning, keep growing! 🎸
                      </p>
                    </td>
                  </tr>
                </table>
              </td>
            </tr>
          </table>
        </body>
        </html>
      `;

      await axios.post(API_ENDPOINTS.sendMail, {
        to: email,
        subject: "Welcome to Ukulele Seekho! 🎵",
        html: emailHtml,
      });
    } catch (error) {
      console.error("Error sending welcome email:", error);
      // Don't show error to user as payment was successful
    }
  };

  const proceedWithCheckout = async () => {
    if (!pendingCheckout) return;

    const { amount, planName, planType } = pendingCheckout;

    try {
      const getKey = await axios.get(API_ENDPOINTS.getKey);

      const createOrder = await axios.post(API_ENDPOINTS.createOrder, {
        amount,
      });

      const key = getKey.data['data']['key'];
      const order = createOrder.data['data'];

      const options = {
        key,
        amount: order['amount'],
        currency: "INR",
        name: "Ukulele Seekho",
        description: `Payment for ${planName}`,
        image: COMMON.appLogoUrl,
        order_id: order['id'],
        handler: async function (response: any) {
          const { data } = await axios.post(
            API_ENDPOINTS.verifyPayment,
            {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            }
          );
          if (data.success) {
            // Send welcome email
            await sendWelcomeEmail(formData.name, formData.email, planName, planType);
            setShowSuccessModal(true);
          } else {
            toast.error("Payment failed. Please try again.");
          }
        },
        notes: {
          address: "Razorpay Corporate Office",
        },
        theme: {
          color: "#3399cc",
        },
      };

      const rzp = new window.Razorpay(options);

      rzp.on('payment.failed', function () {
        setIsProcessingPayment(false);
        setShowFormModal(false);
        setPendingCheckout(null);
      });
      
      rzp.open();
      
      // Close form modal and reset pending checkout
      setShowFormModal(false);
      setPendingCheckout(null);
      setIsProcessingPayment(false);
    } catch (error) {
      console.error("Error during checkout:", error);
      toast.error("An error occurred during checkout. Please try again.");
      setShowFormModal(false);
      setPendingCheckout(null);
      setIsProcessingPayment(false);
    }
  };

  const checkoutHandler = async (
    amount: number,
    planName: string,
    planType: "group" | "oneOnOne",
  ) => {
    // Store checkout details and show form modal
    setPendingCheckout({ amount, planName, planType });
    setShowFormModal(true);
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.email.trim()) {
      toast.error("Please fill in all fields.");
      return;
    }
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setIsProcessingPayment(true);
    proceedWithCheckout();
  };

  const totalGroupPages = Math.ceil(GROUP_PLANS.length / groupPageSize);
  const safeGroupPage =
    groupPage >= totalGroupPages ? Math.max(totalGroupPages - 1, 0) : groupPage;
  const groupSliceStart = safeGroupPage * groupPageSize;
  const visibleGroupPlans = GROUP_PLANS.slice(
    groupSliceStart,
    groupSliceStart + groupPageSize
  );

  useEffect(() => {
    if (activeTab === "group") {
      setGroupPage(0);
    }
  }, [activeTab]);

  const currentPlans =
    activeTab === "group" ? visibleGroupPlans : ONE_ON_ONE_PLANS;

  return (
    <div>
      <Toaster position="top-center" reverseOrder={false} />
      {/* Form Modal */}
      {showFormModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in"
          onClick={() => {
            if (isProcessingPayment) return;
            setShowFormModal(false);
            setPendingCheckout(null);
          }}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-accentNavy/60 backdrop-blur-sm animate-fade-in" />
          
          {/* Modal Content */}
          <div
            className="relative z-10 w-full max-w-md rounded-[32px] border border-white/50 bg-white/95 p-8 shadow-[0_35px_70px_-40px_rgba(9,32,63,0.55)] backdrop-blur-xl animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              type="button"
              onClick={() => {
                if (isProcessingPayment) return;
                setShowFormModal(false);
                setPendingCheckout(null);
              }}
              className="absolute right-4 top-4 rounded-full p-2 text-accentNavy/50 transition-all duration-200 hover:bg-accentNavy/5 hover:text-accentNavy/80 hover:scale-110"
              aria-label="Close"
            >
              <svg
                className="h-5 w-5"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Content */}
            <div className="flex flex-col">
              <h3 className="mb-2 text-2xl font-semibold text-accentNavy">
                Almost there!
              </h3>
              <p className="mb-6 text-sm text-accentNavy/70">
                Please provide your details to proceed with the purchase.
              </p>

              <form onSubmit={handleFormSubmit} className="space-y-4">
                {/* Name Input */}
                <div>
                  <label
                    htmlFor="name"
                    className="mb-2 block text-sm font-semibold text-accentNavy/80"
                  >
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    required
                    className="w-full rounded-xl border-2 border-accentNavy/20 bg-white/80 px-4 py-3 text-accentNavy placeholder-accentNavy/40 transition-all duration-300 focus:border-accentNavy focus:outline-none focus:ring-2 focus:ring-accentNavy/20"
                    placeholder="Enter your full name"
                  />
                </div>

                {/* Email Input */}
                <div>
                  <label
                    htmlFor="email"
                    className="mb-2 block text-sm font-semibold text-accentNavy/80"
                  >
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    required
                    className="w-full rounded-xl border-2 border-accentNavy/20 bg-white/80 px-4 py-3 text-accentNavy placeholder-accentNavy/40 transition-all duration-300 focus:border-accentNavy focus:outline-none focus:ring-2 focus:ring-accentNavy/20"
                    placeholder="Enter your email address"
                  />
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isProcessingPayment}
                  className="mt-6 inline-flex w-full items-center justify-center rounded-full border border-accentNavy/20 bg-accentNavy px-6 py-3 text-sm font-semibold uppercase tracking-[0.25em] text-white shadow-[0_18px_30px_-20px_rgba(9,32,63,0.7)] transition-all duration-300 hover:shadow-[0_20px_45px_-18px_rgba(9,32,63,0.65)] hover:scale-105 disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {isProcessingPayment ? (
                    <div className="flex items-center justify-center">
                      <div className="w-4 h-4 border-2 border-t-transparent border-white rounded-full animate-spin mr-2"></div>
                      Processing...
                    </div>
                  ) : (
                    "Proceed to Payment"
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in"
          onClick={() => setShowSuccessModal(false)}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-accentNavy/60 backdrop-blur-sm animate-fade-in" />
          
          {/* Modal Content */}
          <div
            className="relative z-10 w-full max-w-md rounded-[32px] border border-white/50 bg-white/95 p-8 shadow-[0_35px_70px_-40px_rgba(9,32,63,0.55)] backdrop-blur-xl animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              type="button"
              onClick={() => setShowSuccessModal(false)}
              className="absolute right-4 top-4 rounded-full p-2 text-accentNavy/50 transition-all duration-200 hover:bg-accentNavy/5 hover:text-accentNavy/80 hover:scale-110"
              aria-label="Close"
            >
              <svg
                className="h-5 w-5"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Content */}
            <div className="flex flex-col items-center text-center">
              {/* Logo */}
              <div 
                className="mb-6 flex h-32 w-32 items-center justify-center rounded-full bg-white/80 shadow-lg"
                style={{ 
                  opacity: 0,
                  transform: "scale(0.9)",
                  animation: "scale-in 0.7s ease-out 0.15s forwards"
                }}
              >
                <img
                  src={COMMON.appLogoUrl}
                  alt="Logo"
                  className="h-24 w-24 object-contain"
                  style={{ animation: "bounce-subtle 0.6s ease-in-out 0.5s" }}
                />
              </div>

              {/* Success Icon */}
              <div 
                className="mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-accentGold/10"
                style={{ 
                  opacity: 0,
                  transform: "scale(0.9)",
                  animation: "scale-in 0.5s ease-out 0.3s forwards"
                }}
              >
                <svg
                  className="h-8 w-8 text-accentGold"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2.5"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  style={{
                    opacity: 0,
                    animation: "fade-in 0.5s ease-out 0.4s forwards"
                  }}
                >
                  <path d="M5 13l4 4L19 7" />
                </svg>
              </div>

              {/* Message */}
              <h3 
                className="mb-3 text-2xl font-semibold text-accentNavy"
                style={{ 
                  opacity: 0,
                  transform: "translateY(10px)",
                  animation: "fade-in 0.5s ease-out 0.5s forwards"
                }}
              >
                Welcome aboard!
              </h3>
              <p 
                className="text-base text-accentNavy/70 leading-relaxed"
                style={{ 
                  opacity: 0,
                  transform: "translateY(10px)",
                  animation: "fade-in 0.5s ease-out 0.6s forwards"
                }}
              >
                Your payment was successful. Please check your email for all the information to get started.
              </p>

              {/* CTA Button */}
              <button
                type="button"
                onClick={() => setShowSuccessModal(false)}
                className="mt-8 inline-flex items-center justify-center rounded-full border border-accentNavy/20 bg-accentNavy px-6 py-3 text-sm font-semibold uppercase tracking-[0.25em] text-white shadow-[0_18px_30px_-20px_rgba(9,32,63,0.7)] transition-all duration-300 hover:shadow-[0_20px_45px_-18px_rgba(9,32,63,0.65)] hover:scale-105"
                style={{ 
                  opacity: 0,
                  transform: "translateY(10px)",
                  animation: "slide-up 0.5s ease-out 0.8s forwards"
                }}
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      <section
        id="batches"
        className="bg-gradient-to-b from-primary via-primary/95 to-primary/90 py-20 sm:py-24"
      >

      <div className="mx-auto flex max-w-6xl flex-col gap-14 px-6">
        <Reveal className="space-y-5 text-center">
          <p className="text-xs uppercase tracking-[0.35em] text-accentNavy/45">
            Pick your pace
          </p>
          <h2 className="font-display text-3xl text-accentNavy sm:text-4xl lg:text-5xl">
            Batches built to match your practice rhythm.
          </h2>
          <p className="mx-auto max-w-2xl text-base text-accentNavy/70 lg:text-lg">
            Choose a plan that keeps you inspired, accountable, and connected.
            Every option blends mentorship and performances.
          </p>
        </Reveal>

        <Reveal delay={120} className="flex justify-center">
          <div id="plan-tabs" className="relative inline-flex gap-1 rounded-2xl border border-accentNavy/10 bg-white/50 p-1.5 backdrop-blur">
            <button
              type="button"
              onClick={() => setActiveTab("group")}
              className={[
                "relative z-10 rounded-xl px-6 py-3 text-xs sm:text-sm font-semibold uppercase tracking-[0.18em] transition-all duration-300",
                activeTab === "group"
                  ? "bg-white text-accentNavy shadow-[0_4px_12px_-4px_rgba(9,32,63,0.25)]"
                  : "text-accentNavy/60 hover:text-accentNavy/80",
              ].join(" ")}
            >
              Group Batches
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("oneOnOne")}
              className={[
                "relative z-10 rounded-xl px-6 py-3 text-xs sm:text-sm font-semibold uppercase tracking-[0.18em] transition-all duration-300",
                activeTab === "oneOnOne"
                  ? "bg-white text-accentNavy shadow-[0_4px_12px_-4px_rgba(9,32,63,0.25)]"
                  : "text-accentNavy/60 hover:text-accentNavy/80",
              ].join(" ")}
            >
              1 on 1 Batch
            </button>
          </div>
        </Reveal>

        <div className="relative">
          {activeTab === "group" && totalGroupPages > 1 && (
            <>
              {/* Mobile Arrows */}
              <div className="mb-4 flex justify-center gap-4 md:hidden">
                <button
                  type="button"
                  onClick={() =>
                    setGroupPage((prev) => Math.max(prev - 1, 0))
                  }
                  disabled={safeGroupPage === 0}
                  className="flex h-12 w-12 items-center justify-center rounded-full border border-accentNavy/20 bg-white/95 text-accentNavy shadow-[0_8px_22px_-14px_rgba(9,32,63,0.6)] transition-all duration-200 hover:-translate-x-1 hover:border-accentNavy/40 hover:text-accentNavy disabled:opacity-40 disabled:shadow-none disabled:hover:translate-x-0"
                  aria-label="Previous group plans"
                >
                  <svg
                    className="h-6 w-6"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M15 18l-6-6 6-6" />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={() =>
                    setGroupPage((prev) =>
                      Math.min(prev + 1, Math.max(totalGroupPages - 1, 0))
                    )
                  }
                  disabled={safeGroupPage >= totalGroupPages - 1}
                  className="flex h-12 w-12 items-center justify-center rounded-full border border-accentNavy/20 bg-white/95 text-accentNavy shadow-[0_8px_22px_-14px_rgba(9,32,63,0.6)] transition-all duration-200 hover:translate-x-1 hover:border-accentNavy/40 hover:text-accentNavy disabled:opacity-40 disabled:shadow-none disabled:hover:translate-x-0"
                  aria-label="Next group plans"
                >
                  <svg
                    className="h-6 w-6"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                </button>
              </div>
              {/* Desktop Arrows */}
              <button
                type="button"
                onClick={() =>
                  setGroupPage((prev) => Math.max(prev - 1, 0))
                }
                disabled={safeGroupPage === 0}
                className="absolute -left-16 top-1/2 z-10 hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-accentNavy/20 bg-white/95 text-accentNavy shadow-[0_8px_22px_-14px_rgba(9,32,63,0.6)] transition-all duration-200 hover:-translate-x-1 hover:border-accentNavy/40 hover:text-accentNavy disabled:opacity-40 disabled:shadow-none disabled:hover:translate-x-0 md:flex"
                aria-label="Previous group plans"
              >
                <svg
                  className="h-6 w-6"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M15 18l-6-6 6-6" />
                </svg>
              </button>
              <button
                type="button"
                onClick={() =>
                  setGroupPage((prev) =>
                    Math.min(prev + 1, Math.max(totalGroupPages - 1, 0))
                  )
                }
                disabled={safeGroupPage >= totalGroupPages - 1}
                className="absolute -right-16 top-1/2 z-10 hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-accentNavy/20 bg-white/95 text-accentNavy shadow-[0_8px_22px_-14px_rgba(9,32,63,0.6)] transition-all duration-200 hover:translate-x-1 hover:border-accentNavy/40 hover:text-accentNavy disabled:opacity-40 disabled:shadow-none disabled:hover:translate-x-0 md:flex"
                aria-label="Next group plans"
              >
                <svg
                  className="h-6 w-6"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </button>
            </>
          )}
          {activeTab === "group" ? (
            <>
              <div
                id="pricing-cards"
                className="grid flex-1 gap-6 sm:grid-cols-2 lg:grid-cols-3"
              >
                {currentPlans.map(
                  (
                    {
                      name,
                      cadence,
                      mrp,
                      offerPrice,
                      description,
                      highlights,
                      isFeatured,
                      cta,
                    },
                    index
                  ) => (
                    <Reveal key={name} delay={index * 90} className="h-full">
                      <article
                        className={[
                          "group relative flex h-full flex-col rounded-[32px] border border-white/50 bg-white/90 p-8 text-left shadow-[0_35px_70px_-40px_rgba(9,32,63,0.45)] transition-transform duration-500 hover:-translate-y-2",
                          isFeatured
                            ? "lg:-translate-y-3 lg:shadow-[0_40px_90px_-45px_rgba(9,32,63,0.55)]"
                            : "",
                        ].join(" ")}
                      >
                        {isFeatured && (
                          <span className="absolute right-6 top-6 rounded-full border border-accentNavy/20 bg-accentNavy/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.25em] text-accentNavy/70">
                            Most loved
                          </span>
                        )}
                        <div className="space-y-2">
                          <span className="text-xs font-semibold uppercase tracking-[0.35em] text-accentNavy/45">
                            {cadence}
                          </span>
                          <h3 className="text-2xl font-semibold text-accentNavy">
                            {name}
                          </h3>
                          <p className="text-sm text-accentNavy/65 lg:text-base">
                            {description}
                          </p>
                        </div>

                        <div className="mt-6 flex flex-col gap-1">
                          <div className="flex items-baseline gap-2">
                            <span className="text-3xl font-bold tracking-tight text-accentNavy">
                              {offerPrice}
                            </span>
                            <span className="text-sm font-semibold uppercase tracking-[0.2em] text-accentNavy/60">
                              /seat
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-lg text-accentNavy/50 line-through">
                              {mrp !== offerPrice ? mrp : ""}
                            </span>
                            {mrp !== offerPrice && (
                              <span className="text-xs font-semibold text-accentGold">
                                {Math.round(
                                  ((parseFloat(mrp.replace(/[^0-9.]/g, "")) -
                                    parseFloat(
                                      offerPrice.replace(/[^0-9.]/g, "")
                                    )) /
                                    parseFloat(mrp.replace(/[^0-9.]/g, ""))) *
                                    100
                                )}
                                % OFF
                              </span>
                            )}
                          </div>
                        </div>

                        <ul className="mt-6 flex flex-col gap-3 text-sm text-accentNavy/75">
                          {highlights.map((highlight) => (
                            <li
                              key={highlight}
                              className="flex items-center gap-3"
                            >
                              <span className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-accentNavy/15 bg-white/70 text-xs font-semibold text-accentNavy/70">
                                ✓
                              </span>
                              <span>{highlight}</span>
                            </li>
                          ))}
                        </ul>

                        <button
                          type="button"
                          onClick={() =>
                            checkoutHandler(
                              Number(offerPrice.replace(/[^0-9.]/g, "")),
                              name,
                              activeTab === "group" ? "group" : "oneOnOne",
                            )
                          }
                          className={[
                            "mt-8 inline-flex w-full items-center justify-center rounded-full border border-accentNavy/20 px-4 py-3 text-sm font-semibold uppercase tracking-[0.25em] transition-all duration-300",
                            isFeatured
                              ? "bg-accentNavy text-white shadow-[0_18px_30px_-20px_rgba(9,32,63,0.7)] hover:shadow-[0_20px_45px_-18px_rgba(9,32,63,0.65)]"
                              : "bg-white/80 text-accentNavy hover:bg-white",
                          ].join(" ")}
                        >
                          {cta}
                        </button>
                      </article>
                    </Reveal>
                  )
                )}
              </div>

              {totalGroupPages > 1 && (
                <div className="mt-8 flex items-center justify-center gap-1.5 text-accentNavy/70">
                  {Array.from({ length: totalGroupPages }).map((_, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => setGroupPage(index)}
                      className={[
                        "h-2.5 w-2.5 rounded-full transition-colors duration-200",
                        index === safeGroupPage
                          ? "bg-accentNavy"
                          : "bg-accentNavy/20 hover:bg-accentNavy/40",
                      ].join(" ")}
                      aria-label={`Go to group plan set ${index + 1}`}
                    />
                  ))}
                </div>
              )}
            </>
          ) : (
          <div
            id="pricing-cards"
            className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3"
          >
            {currentPlans.map(
              (
                {
                  name,
                  cadence,
                  mrp,
                  offerPrice,
                  description,
                  highlights,
                  isFeatured,
                  cta,
                },
                index
              ) => (
                <Reveal key={name} delay={index * 90} className="h-full">
                  <article
                    className={[
                      "group relative flex h-full flex-col rounded-[32px] border border-white/50 bg-white/90 p-8 text-left shadow-[0_35px_70px_-40px_rgba(9,32,63,0.45)] transition-transform duration-500 hover:-translate-y-2",
                      isFeatured
                        ? "lg:-translate-y-3 lg:shadow-[0_40px_90px_-45px_rgba(9,32,63,0.55)]"
                        : "",
                    ].join(" ")}
                  >
                    {isFeatured && (
                      <span className="absolute right-6 top-6 rounded-full border border-accentNavy/20 bg-accentNavy/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.25em] text-accentNavy/70">
                        Most loved
                      </span>
                    )}
                    <div className="space-y-2">
                      <span className="text-xs font-semibold uppercase tracking-[0.35em] text-accentNavy/45">
                        {cadence}
                      </span>
                      <h3 className="text-2xl font-semibold text-accentNavy">
                        {name}
                      </h3>
                      <p className="text-sm text-accentNavy/65 lg:text-base">
                        {description}
                      </p>
                    </div>

                    <div className="mt-6 flex flex-col gap-1">
                      <div className="flex items-baseline gap-2">
                        <span className="text-3xl font-bold tracking-tight text-accentNavy">
                          {offerPrice}
                        </span>
                        <span className="text-sm font-semibold uppercase tracking-[0.2em] text-accentNavy/60">
                          /seat
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-lg text-accentNavy/50 line-through">
                          {mrp !== offerPrice ? mrp : ""}
                        </span>
                        {mrp !== offerPrice && (
                          <span className="text-xs font-semibold text-accentGold">
                            {Math.round(
                              ((parseFloat(mrp.replace(/[^0-9.]/g, "")) -
                                parseFloat(offerPrice.replace(/[^0-9.]/g, ""))) /
                                parseFloat(mrp.replace(/[^0-9.]/g, ""))) *
                              100
                            )}
                            % OFF
                          </span>
                        )}
                      </div>
                    </div>

                    <ul className="mt-6 flex flex-col gap-3 text-sm text-accentNavy/75">
                      {highlights.map((highlight) => (
                        <li key={highlight} className="flex items-center gap-3">
                          <span className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-accentNavy/15 bg-white/70 text-xs font-semibold text-accentNavy/70">
                            ✓
                          </span>
                          <span>{highlight}</span>
                        </li>
                      ))}
                    </ul>

                    <button
                      type="button"
                      onClick={() =>
                        checkoutHandler(
                          Number(offerPrice.replace(/[^0-9.]/g, "")),
                          name,
                          "oneOnOne"
                        )
                      }
                      className={[
                        "mt-8 inline-flex w-full items-center justify-center rounded-full border border-accentNavy/20 px-4 py-3 text-sm font-semibold uppercase tracking-[0.25em] transition-all duration-300",
                        isFeatured
                          ? "bg-accentNavy text-white shadow-[0_18px_30px_-20px_rgba(9,32,63,0.7)] hover:shadow-[0_20px_45px_-18px_rgba(9,32,63,0.65)]"
                          : "bg-white/80 text-accentNavy hover:bg-white",
                      ].join(" ")}
                    >
                      {cta}
                    </button>
                  </article>
                </Reveal>
              )
            )}
          </div>
        )}

        <Reveal delay={280}>
          <p className="mt-8 text-center text-base sm:text-lg lg:text-xl text-accentNavy/70 max-w-3xl mx-auto leading-relaxed">
            Still not sure which plan fits your routine best?{" "}
            <a
              href="#contact"
              className="font-semibold text-accentNavy hover:text-accentGold transition-colors duration-200 hover:underline"
            >
              Contact us
            </a>{" "}
            and we&apos;ll help you choose the right starting point.
          </p>
        </Reveal>
      </div>
      </div>
    </section>
    </div>
  );
};

export default Plans;
